import { yupResolver } from "@hookform/resolvers/yup";
import { Flex, Image, Text } from "@wayflyer/flyui";
import { RhfDropdownField } from "@wayflyer/flyui-hook-form";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useCustomerAppConfig } from "undercarriage";
import * as Yup from "yup";

import { AuthCallbackPath } from "../../../api-types/connector-callbacks";
import { usePlatformConnectorProperties } from "../../../hooks/connector";
import type { AmazonData } from "../common/connect-by-post";
import { useConnectByPost } from "../common/connect-by-post";
import { ConnectionsList } from "../common/connections";
import { OAuthErrorBanner } from "../common/oauth-error-banner";
import { MESSAGES } from "../message-definitions";

import { useAmazonRegionOptions } from "./amazon-region-options";

const CONNECTOR_SLUG = "amazonrutter";

interface AmazonConnectProps {
  postConnectPath: AuthCallbackPath;
  onInitiateConnection?: () => void;
}
export const AmazonConnect = ({ postConnectPath, onInitiateConnection }: AmazonConnectProps) => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: connectorProperties } = usePlatformConnectorProperties(customerDomain, CONNECTOR_SLUG, postConnectPath);

  const connectByPost = useConnectByPost();

  const { regionOptions, regionCodes } = useAmazonRegionOptions(intl);

  const schema = Yup.object({
    value: Yup.string()
      .required(
        intl.formatMessage({
          id: "funding-application: valid marketplace hint",
          defaultMessage: "Please select an Amazon seller region.",
        })
      )
      .oneOf(regionCodes),
  });

  const amazonForm = useForm<AmazonData>({
    resolver: yupResolver(schema),
  });

  const connectHandler = async (formData: AmazonData) => {
    setIsSubmitting(true);
    if (connectorProperties.postPath) {
      onInitiateConnection?.();
      await connectByPost(connectorProperties.postPath, formData);
    }
  };

  return (
    <Flex direction="column" justify="flex-start">
      <Flex direction="column" gap="8">
        <OAuthErrorBanner connectorSlug={CONNECTOR_SLUG} />
        <Text>{intl.formatMessage(MESSAGES.ecommerceDataUsage, { platform: "Amazon" })}</Text>
        <ConnectionsList
          connectorProperties={connectorProperties}
          connectForm={
            <form onSubmit={amazonForm.handleSubmit(connectHandler)}>
              <Flex direction="row" gap="3" align="center">
                <RhfDropdownField
                  label={intl.formatMessage({
                    id: "funding-application: seller region - label",
                    defaultMessage: "Select Amazon seller region",
                  })}
                  labelIcon={
                    connectorProperties.connectionLogoSrc && <Image src={connectorProperties.connectionLogoSrc} />
                  }
                  form={amazonForm}
                  name="value"
                  options={regionOptions}
                  inlineButton={{
                    label: intl.formatMessage({
                      id: "funding-application: log in to amazon button label",
                      defaultMessage: "Log in to Amazon",
                    }),
                    type: "submit",
                    loading: isSubmitting,
                  }}
                />
              </Flex>
            </form>
          }
        />
      </Flex>
    </Flex>
  );
};
